import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExpandedState } from "@tanstack/react-table";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useConstant } from "@hooks/useConstant";
import { View, ViewDensity } from "@common/types/views/views";
import { EliminatingEntry } from "@common/schemas/domains/accounting/eliminatingEntry.schema";
import { useEntities } from "@hooks/useEntities";
import { LoaderOnInitialLoading } from "@design-system/LoaderOnInitialLoading/LoaderOnInitialLoading";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Card } from "@design-system/Card/Card";
import { CardSection } from "@design-system/Card/CardSection";
import { Button } from "@design-system/Button/Button";
import { WithHotkey } from "@design-system/Button/WithHotkey";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CollapseIcon, ExpandIcon } from "@icons";
import { EntityFilterCombobox } from "@components/entity/EntityFilter/EntityFilterCombobox";
import { EntityFilterType } from "@components/entity/EntityFilter/types";
import { glDirectionRadios } from "@components/pageHeader/filters/Tabs/AmountFilter/directionRadios";
import { useEliminationDetailReportQuery } from "@api/accountingReports.api";
import { useListEliminatingEntriesQuery } from "@api/eliminatingEntries.api";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { AccountingReportFiltersForm } from "../AccountingReportFiltersForm";
import { AccountingReportHeader } from "../AccountingReportHeader";
import { getOptionalReportDateRangeLabel } from "@common/helpers/reports";
import { AccountingReportFooter } from "../AccountingReportFooter";
import {
    buildLocalReportChrome,
    useGeneratedReportFooterText,
} from "../localReportChrome";
import { useAccountingTabContext } from "../useAccountingTab";
import { useExpandAllState } from "../useExpandAllState";
import { useConsolidatedReportView } from "../useConsolidatedReportView";
import { EliminatingEntryDrawer } from "../eliminatingEntryDrawer/EliminatingEntryDrawer";
import { buildEliminationDetailRows } from "./eliminationDetail.lib";
import {
    EliminationDetailColumn,
    EliminationDetailTableMeta,
    EliminationDetailViewRow,
    eliminationDetailViewConfig,
} from "./eliminationDetailViewConfig";
import styles from "./EliminationDetailTable.module.scss";

const defaultView: View = {
    columns: [
        { id: EliminationDetailColumn.NAME, options: { pin: "left" } },
        { id: EliminationDetailColumn.ENTITY },
        { id: EliminationDetailColumn.SOURCE },
        { id: EliminationDetailColumn.REFERENCE },
        { id: EliminationDetailColumn.DESCRIPTION },
        { id: EliminationDetailColumn.COUNTERPARTY },
        { id: EliminationDetailColumn.CLASS },
        { id: EliminationDetailColumn.SPLIT },
        { id: EliminationDetailColumn.AMOUNT, options: { pin: "right" } },
        { id: EliminationDetailColumn.BALANCE, options: { pin: "right" } },
    ],
    filters: {},
    options: {
        selection: false,
        bulkActions: false,
        density: ViewDensity.LG,
    },
};

export const EliminationDetailPreviewPage: React.FC = () => {
    const { filters, setFilters } = useAccountingTabContext();
    const { consolidationGroup, parentEntity } = useConsolidatedReportView({
        filters,
        setFilters,
    });
    const allEntities = useEntities();
    // The group's member entities (parent first) — the eliminating-entry drawer
    // scopes each line's account picker to the chosen member's chart.
    const memberEntities = useMemo(() => {
        if (!parentEntity || !consolidationGroup) {
            return [];
        }
        const memberUuids = new Set(
            consolidationGroup.members.map((member) => member.entityUuid),
        );
        const otherMembers = allEntities.filter(
            (entity) =>
                memberUuids.has(entity.uuid) &&
                entity.uuid !== parentEntity.uuid,
        );
        return [parentEntity, ...otherMembers];
    }, [allEntities, consolidationGroup, parentEntity]);
    const [searchParams] = useSearchParams();
    const accountCode = searchParams.get("accountCode");
    const parsedAccountCode = accountCode ? Number(accountCode) : undefined;
    const selectedAccountCode =
        parsedAccountCode !== undefined && Number.isFinite(parsedAccountCode)
            ? parsedAccountCode
            : undefined;

    // The account filter is the single source of truth for account scoping (so
    // it shows as an applied filter, like the general ledger). Scope the report
    // server-side only when exactly one account is selected; multi-select falls
    // back to fetching all and filtering client-side.
    const accountCodeForQuery =
        filters.accountCodes?.length === 1
            ? filters.accountCodes[0]
            : undefined;

    const eliminationDetail = useEliminationDetailReportQuery({
        workspaceId: filters.workspaceId,
        consolidationGroupId: filters.consolidationGroupId,
        startDate: filters.startDate,
        endDate: filters.endDate,
        accountCode: accountCodeForQuery,
    });
    const eliminatingEntries = useListEliminatingEntriesQuery({
        workspaceId: filters.workspaceId,
        groupId: filters.consolidationGroupId,
    });

    const [drawerState, setDrawerState] = useState<{
        open: boolean;
        entry?: EliminatingEntry;
    }>({ open: false });
    const openEliminatingEntry = useCallback(
        (manualJournalEntryId: string) => {
            const entry = eliminatingEntries.data?.find(
                ({ id }) => id === manualJournalEntryId,
            );
            if (entry) {
                setDrawerState({ open: true, entry });
            }
        },
        [eliminatingEntries.data],
    );
    const freshEliminatingEntryIds = useMemo(() => {
        if (
            !eliminatingEntries.isSuccess ||
            eliminatingEntries.isFetching ||
            !eliminatingEntries.data
        ) {
            return null;
        }
        return new Set(eliminatingEntries.data.map(({ id }) => id));
    }, [
        eliminatingEntries.data,
        eliminatingEntries.isFetching,
        eliminatingEntries.isSuccess,
    ]);
    const canOpenEliminatingEntry = useCallback(
        (manualJournalEntryId: string) =>
            freshEliminatingEntryIds?.has(manualJournalEntryId) ?? false,
        [freshEliminatingEntryIds],
    );
    const tableMeta = useMemo<EliminationDetailTableMeta>(
        () => ({
            callbacks: freshEliminatingEntryIds
                ? {
                      onOpenEliminatingEntry: openEliminatingEntry,
                      canOpenEliminatingEntry,
                  }
                : {},
        }),
        [
            canOpenEliminatingEntry,
            freshEliminatingEntryIds,
            openEliminatingEntry,
        ],
    );

    const [entityFilter, setEntityFilter] = useState<EntityFilterType>({});

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_ELIMINATION_DETAIL_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const canAuthorEliminations = Boolean(
        filters.consolidationGroupId && filters.workspaceId && parentEntity,
    );

    // Landing on a bare /accounting/elimination-detail URL (no consolidated
    // params) would render an empty page. Backfill the active group's params —
    // consolidationGroupId also derives workspaceId in the filters — so the
    // report and authoring UI show without the user having to drill in from a
    // consolidated report.
    useEffect(() => {
        if (filters.consolidationGroupId || !consolidationGroup) {
            return;
        }
        setFilters((current) => ({
            ...current,
            consolidationGroupId: consolidationGroup.id,
            entityId: current.entityId ?? parentEntity?.id,
        }));
    }, [
        filters.consolidationGroupId,
        consolidationGroup,
        parentEntity,
        setFilters,
    ]);

    // Close the drawer when the group/workspace filter changes: the drawer has
    // no backdrop, so an open Edit drawer would otherwise keep the previous
    // group's `entry` while its group/workspace props switch to the new group,
    // and Save would post that entry's id against the new group's params.
    useEffect(() => {
        setDrawerState({ open: false });
    }, [filters.consolidationGroupId, filters.workspaceId]);

    // Drill-through URLs carry an `accountCode`; seed it into the account filter
    // so it renders as an applied filter (matching the general ledger) and
    // drives both the report scope and the client-side filter.
    useEffect(() => {
        if (selectedAccountCode === undefined) {
            return;
        }
        setFilters((current) => ({
            ...current,
            accountCodes: [selectedAccountCode],
        }));
    }, [selectedAccountCode, setFilters]);

    // Group the backend rows into the general-ledger shape (expandable account
    // row wrapping Beginning balance, the lines, and Ending balance) and apply
    // the entity + general-ledger-style filters client-side. See
    // buildEliminationDetailRows (unit-tested) — the running balance is
    // recomputed over the filtered lines so it ties out to what's shown.
    const groupedRows = useMemo<EliminationDetailViewRow[]>(
        () =>
            buildEliminationDetailRows(eliminationDetail.data?.rows ?? [], {
                openingBalances: eliminationDetail.data?.openingBalances,
                entityIds: entityFilter.entityIds,
                accountCodes: filters.accountCodes,
                counterpartyIds: filters.counterpartiesIds,
                journalEntrySources: filters.journalEntrySources,
                minAmount: filters.minAmount,
                maxAmount: filters.maxAmount,
                direction: filters.direction,
            }),
        [eliminationDetail.data, entityFilter, filters],
    );

    // Account rows expand by default; the toolbar button toggles all at once
    // (mirrors the general ledger).
    const expandAllState = useMemo<ExpandedState>(
        () =>
            Object.fromEntries(
                groupedRows.map((row) => [row.id, true] as const),
            ),
        [groupedRows],
    );
    const {
        expanded,
        setExpanded,
        isCollapsed,
        toggleAll: handleToggleCollapse,
    } = useExpandAllState(expandAllState);

    const generationDate = useConstant(() => new Date());
    const footerText = useGeneratedReportFooterText({ generationDate });
    const chrome = useMemo(
        () =>
            buildLocalReportChrome({
                title: parentEntity
                    ? `${parentEntity.name} (Consolidated)`
                    : "Consolidated",
                subtitle: "Elimination Ledger",
                subtitle2: getOptionalReportDateRangeLabel(
                    filters.startDate,
                    filters.endDate,
                ),
                footer: footerText,
            }),
        [parentEntity, filters.startDate, filters.endDate, footerText],
    );

    return (
        <>
            <AccountingReportFiltersForm
                filters={filters}
                setFilters={setFilters}
                consolidationGroup={consolidationGroup}
                pinnedPresets={pinnedPresets}
                onTogglePin={onTogglePin}
                requireDateSelection
                hideSearch
                hideClassFilter
                showCyclesSelect={false}
                hideLedgerSelect
                hideCreateJournalEntryButton
                hideEntityFilter
                showAccountFilter
                showCounterpartyFilter
                showJournalEntrySourceFilter
                showAmountFilter
                directionRadios={glDirectionRadios}
                additionalButtons={
                    <>
                        {filters.consolidationGroupId &&
                            filters.workspaceId && (
                                <>
                                    <EntityFilterCombobox
                                        filters={entityFilter}
                                        onChange={setEntityFilter}
                                        onlyBusiness
                                    />
                                    <Tooltip
                                        key={
                                            isCollapsed ? "collapse" : "expand"
                                        }
                                        content={
                                            isCollapsed
                                                ? "Expand all"
                                                : "Collapse all"
                                        }
                                    >
                                        <Button
                                            iconOnly
                                            variant="primary"
                                            size="md"
                                            onClick={handleToggleCollapse}
                                            aria-label={
                                                isCollapsed
                                                    ? "Expand all"
                                                    : "Collapse all"
                                            }
                                        >
                                            {isCollapsed ? (
                                                <ExpandIcon />
                                            ) : (
                                                <CollapseIcon />
                                            )}
                                        </Button>
                                    </Tooltip>
                                </>
                            )}
                        {canAuthorEliminations && (
                            <WithHotkey
                                hotKey="alt+n"
                                hotKeyOptions={{
                                    description: "Create eliminating entry",
                                }}
                            >
                                <Button
                                    variant="primary"
                                    size="md"
                                    onClick={() =>
                                        setDrawerState({ open: true })
                                    }
                                >
                                    Create eliminating entry
                                </Button>
                            </WithHotkey>
                        )}
                    </>
                }
            />
            <LoaderOnInitialLoading
                queryResult={eliminationDetail}
                dimWhileFetching
            >
                {filters.consolidationGroupId && filters.workspaceId ? (
                    <Card stacked sharpEdges fullPage>
                        <CardSection className={styles.header}>
                            <AccountingReportHeader chrome={chrome} />
                        </CardSection>
                        <ViewTable
                            data={groupedRows}
                            view={defaultView}
                            config={eliminationDetailViewConfig}
                            tableMeta={tableMeta}
                            className={styles.eliminationDetailTable}
                            emptyView={() => (
                                <EmptyState header="No matched eliminations for this period" />
                            )}
                            rowBorders={false}
                            enableColumnResizing
                            expanded={expanded}
                            onExpandedChange={setExpanded}
                            nestingIndentPx={16}
                        />
                        <AccountingReportFooter chrome={chrome} />
                    </Card>
                ) : (
                    <EmptyState header="Select All entities (Consolidated) to view elimination ledger" />
                )}
            </LoaderOnInitialLoading>
            {canAuthorEliminations &&
                parentEntity &&
                filters.workspaceId &&
                filters.consolidationGroupId && (
                    <EliminatingEntryDrawer
                        open={drawerState.open}
                        onClose={() => setDrawerState({ open: false })}
                        workspaceId={filters.workspaceId}
                        groupId={filters.consolidationGroupId}
                        entities={memberEntities}
                        entry={drawerState.entry}
                    />
                )}
        </>
    );
};
